/* eslint-disable no-unused-vars */
/* eslint-disable react/no-unused-prop-types */
import {
    Form,
    Alert,
    Space,
    Input,
    Button,
    Typography,
    FormInstance,
} from 'antd';

import { Link } from 'react-router-dom';
import { FC, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { IFieldProps } from '../widgets/PhoneNumber/interface';
import { ICreateAccount } from '../interfaces/auth.interface';
import { startSignup, useAuthStore } from '../store/auth.store';
import { Footer } from '../components/Onboarding/Footer';
import { Header } from '../components/Onboarding/Header';
import { PhoneNumber } from '../widgets/PhoneNumber';
import { ERole } from '../enums/auth.enum';

import IMAGES from '../const/images';

interface IProps {
    t: TFunction;
}

interface IFormContainerProps extends IProps {
    form: FormInstance;
    error: string | null;
    hasToken: boolean;
    loading: boolean;
    defaultCountryCode: string;
    onSubmit: (values: any) => void;
    setDefaultCountryCode: (value: string) => void;
}

const { Title, Text } = Typography;

const FormContainer: FC<IFormContainerProps> = ({
    t,
    form,
    error,
    hasToken,
    loading,
    onSubmit,
    defaultCountryCode,
    setDefaultCountryCode,
}) => {
    const pattern = /^[A-ZáéíóúÁÉÍÓÚñÑ ]+$/i;

    const fieldProps: IFieldProps = {
        label: t('CREATE_ACCOUNT.PHONE'),
        keyCountryCode: 'countryCode',
        keyMobile: 'mobile',
    };

    const handleSelect = (value: string) => {
        form.setFieldValue('countryCode', value);
        setDefaultCountryCode(value);
    };

    return (
        <Form
            form={form}
            onFinish={onSubmit}
            layout="vertical"
        >
            {
                error && (
                    <Alert
                        message={t(error)}
                        type="error"
                        style={{ marginBottom: 24 }}
                    />
                )
            }
            <Form.Item
                name="name"
                label={t('CREATE_ACCOUNT.FIRST_NAME')}
                rules={[
                    {
                        required: true,
                        message: t('CREATE_ACCOUNT.FIELD_REQUIRED'),
                    },
                    {
                        pattern: new RegExp(pattern),
                        message: t('CREATE_ACCOUNT.INVALID_FIELD_STRING'),
                    },
                ]}
            >
                <Input placeholder={t('CREATE_ACCOUNT.FIRST_NAME')} />
            </Form.Item>

            <Form.Item
                name="lastName"
                label={t('CREATE_ACCOUNT.LAST_NAME')}
                rules={[
                    {
                        required: true,
                        message: t('CREATE_ACCOUNT.FIELD_REQUIRED'),
                    },
                    {
                        pattern: new RegExp(pattern),
                        message: t('CREATE_ACCOUNT.INVALID_FIELD_STRING'),
                    },
                ]}
            >
                <Input placeholder={t('CREATE_ACCOUNT.LAST_NAME')} />
            </Form.Item>

            <PhoneNumber
                form={form}
                fieldProps={fieldProps}
                handleSelect={handleSelect}
                defaultCountryCode={defaultCountryCode}
            />

            <Form.Item
                name="email"
                label={t('CREATE_ACCOUNT.EMAIL')}
                rules={[
                    {
                        type: 'email',
                        message: t('CREATE_ACCOUNT.INVALID_EMAIL'),
                    },
                    {
                        required: true,
                        message: t('CREATE_ACCOUNT.FIELD_REQUIRED'),
                    },
                ]}
            >
                <Input
                    placeholder={t('CREATE_ACCOUNT.EMAIL')}
                    disabled={hasToken}
                />
            </Form.Item>

            {
                !hasToken && (
                    <Form.Item
                        name="password"
                        label={t('CREATE_ACCOUNT.PASSWORD')}
                        rules={[{ required: true, message: t('CREATE_ACCOUNT.FIELD_REQUIRED') }]}
                    >
                        <Input.Password
                            placeholder={t('CREATE_ACCOUNT.PASSWORD')}
                            type="password"
                        />
                    </Form.Item>
                )
            }

            <Button
                block
                type="primary"
                htmlType="submit"
                loading={loading}
            >
                {t('CREATE_ACCOUNT.CONTINUE')}
            </Button>
        </Form>
    );
};

const LoginToAccount: FC<IProps> = ({ t }) => (
    <div className="flex-row center-row-center">
        <Space size={4}>
            <Text type="secondary">
                {t('IS_MEMBER')}
            </Text>
            <Link to="/login" key="reset">
                {t('CREATE_ACCOUNT.LOGIN')}
            </Link>
        </Space>
    </div>
);

const SignupScreen = () => {
    const { t } = useTranslation('onboarding');

    const [form] = Form.useForm();

    const loading = useAuthStore((state) => state.loading);
    const error = useAuthStore((state) => state.error);
    const user = useAuthStore((state) => state.data);

    const [hasToken, setHasToken] = useState<boolean>(false);
    const [defaultCountryCode, setDefaultCountryCode] = useState<string>('+593');

    useEffect(() => {
        if (!user?._id) return;
        form.setFieldsValue({ ...user });
        setHasToken(true);
    }, [user, form]);

    const onSubmit = async (values: any) => {
        const payload: ICreateAccount = {
            ...values,
            appVersion: '1.0',
            deviceType: 'WEB',
            role: ERole.DELIVERY_PARTNER,
            countryCode: defaultCountryCode,
        };

        if (hasToken) {
            delete values.email;
        } else {
            startSignup(payload);
        }
    };

    return (
        <div
            style={{ backgroundImage: `url(${IMAGES.login.spirals})` }}
            className="public-layout"
        >
            <div className="public-layout__container">
                <div className="public-layout__container--form">
                    <Space direction="vertical" size={40}>
                        <Header />
                        <Title
                            level={2}
                            style={{
                                color: '#6C8EEF',
                                marginBottom: 0,
                                textAlign: 'center',
                            }}
                        >
                            {t('WANT_DELIVERY_PARTNER')}
                        </Title>
                        <Space direction="vertical" size={24}>
                            <FormContainer
                                t={t}
                                form={form}
                                error={error}
                                hasToken={hasToken}
                                loading={loading}
                                onSubmit={onSubmit}
                                defaultCountryCode={defaultCountryCode}
                                setDefaultCountryCode={setDefaultCountryCode}
                            />
                            <LoginToAccount t={t} />
                        </Space>
                        <Footer />
                    </Space>
                </div>
            </div>
        </div>
    );
};

export { SignupScreen };
